import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { Plus as PlusIcon } from '../../assets/icons/plus';
import { DesignationsFilter } from '../../features/designations/Designations/DesignationsFilter';
import { columns } from './columns';
import PermissionChecker from '../../components/common/PermissionChecker';
import {
    clearFilters,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    setFilters,
    setInitialFilters,
} from '../../store/slices/tableController';
import CustomTable from '../../components/common/CustomTable';
import { styles } from './styles';
import { useSearchDesignationsMutation, useListDesignationsMutation } from '../../api/designations';

const Designations = (props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchDesignations, { isLoading: isSearchLoading }] = useSearchDesignationsMutation();
    const [listDesignations, { isLoading: isListLoading }] = useListDesignationsMutation();
    const controller = useSelector((state) => state.tableController);
    const { enqueueSnackbar } = useSnackbar();
    const [designations, setDesignations] = useState([]);

    useEffect(() => {
        dispatch(
            setInitialFilters([
                {
                    property: {
                        name: 'name',
                        type: 'string',
                    },
                    operator: {
                        value: '',
                        typesAccepted: [],
                    },
                    value: '',
                },
            ]),
        );
    }, []);

    const fetchData = useCallback(
        async (searched, filters, sort, sortBy, page, pageSize) => {
            try {
                const query = new URLSearchParams({});
                if (filters.length) {
                    query.append('c', filters[0].property);
                    query.append('q', filters[0].value);
                }
                if (sortBy) {
                    query.append('sort', sortBy);
                    query.append('order', sort);
                }
                query.append('offset', page * pageSize);
                query.append('max', pageSize);

                let response;
                if (searched) {
                    response = await searchDesignations(query).unwrap();
                } else {
                    response = await listDesignations(query).unwrap();
                }

                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                setDesignations(response);
            } catch (err) {
                if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
                else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
            }
        },
        [searchDesignations, listDesignations, dispatch, enqueueSnackbar],
    );

    useEffect(() => {
        if (fetchData)
            fetchData(
                controller.searched,
                controller.filters,
                controller.sort,
                controller.sortBy,
                controller.page,
                controller.pageSize,
            );
    }, [
        controller.searched,
        controller.filters,
        controller.sort,
        controller.sortBy,
        controller.page,
        controller.pageSize,
        fetchData,
    ]);

    const handleFiltersApply = (newFilters) => {
        const parsedFilters = newFilters.map((filter) => ({
            property: filter.property.name,
            value: filter.value,
            operator: filter.operator.value,
        }));

        dispatch(setFilters(parsedFilters));
    };

    const handleFiltersClear = () => {
        dispatch(clearFilters());
    };

    const handlePageChange = (newPage) => {
        dispatch(onPageChange(newPage - 1));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(onPageSizeChange(newPageSize));
    };

    const handleSortChange = (event, property) => {
        const isAsc = controller.sortBy === property && controller.sort === 'asc';
        dispatch(
            onSortChange({
                sort: isAsc ? 'desc' : 'asc',
                sortBy: property,
            }),
        );
    };

    const handleRowClick = (row) => {
        navigate(`/settings/designations/${row}`);
    };

    const isLoading = isSearchLoading || isListLoading;

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Designations</Typography>
                <PermissionChecker
                    requiredPermissions={['ROLE_BASE_PERMISSION_INDEX_CREATE_DESIGNATION']}
                    noAuthRedirect={false}
                >
                    <Button
                        color="primary"
                        onClick={() => navigate('/settings/designations/add')}
                        size="large"
                        startIcon={<PlusIcon fontSize="small" />}
                        variant="contained"
                    >
                        Add Designation
                    </Button>
                </PermissionChecker>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <DesignationsFilter
                            disabled={false}
                            filters={controller.filters}
                            onFiltersApply={handleFiltersApply}
                            onFiltersClear={handleFiltersClear}
                            filterProperties={columns}
                            initialFilters={controller.initialFilters}
                        />
                        <CustomTable
                            onRowClick={handleRowClick}
                            isLoading={isLoading}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onSortChange={handleSortChange}
                            page={controller.page + 1}
                            columns={columns}
                            rows={designations.designationInstanceList}
                            rowsCount={designations.designationInstanceCount}
                            sort={controller.sort}
                            sortBy={controller.sortBy}
                            showRowSelector={false}
                            showPagignation
                            keyColumn="id"
                            action={() =>
                                fetchData(
                                    controller.searched,
                                    controller.filters,
                                    controller.sort,
                                    controller.sortBy,
                                    controller.page,
                                    controller.pageSize,
                                )
                            }
                        />
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default Designations;
